"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { CheckCircle2, XCircle, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOCATIONS = ["Stilfontein", "Orkney", "Vaal Reefs", "Potchefstroom", "Other"];

type Step = "screening" | "disqualified" | "questions" | "contact" | "done";

interface FormData {
  interestedWithBus: string;
  location: string;
  otherLocation: string;
  parentName: string;
  email: string;
  phone: string;
}

const initialData: FormData = {
  interestedWithBus: "",
  location: "",
  otherLocation: "",
  parentName: "",
  email: "",
  phone: "",
};

function ProgressBar({ step }: { step: Step }) {
  const steps: Step[] = ["screening", "questions", "contact", "done"];
  const current = steps.indexOf(step === "disqualified" ? "done" : step);
  const total = steps.length - 1;
  const pct = current < 0 ? 0 : Math.round((current / total) * 100);

  return (
    <div className="w-full mb-8">
      <div className="flex justify-between text-xs text-muted-foreground mb-2">
        <span>Survey Progress</span>
        <span>{pct}%</span>
      </div>
      <div className="w-full bg-border rounded-full h-2">
        <div
          className="bg-school-navy h-2 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function RadioOption({
  id,
  name,
  value,
  label,
  checked,
  onChange,
}: {
  id: string;
  name: string;
  value: string;
  label: string;
  checked: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
        checked
          ? "border-school-navy bg-school-navy/5"
          : "border-border hover:border-school-navy/40 bg-background"
      }`}
    >
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
          checked ? "border-school-navy" : "border-muted-foreground"
        }`}
      >
        {checked && (
          <div className="w-2.5 h-2.5 rounded-full bg-school-navy" />
        )}
      </div>
      <span className="font-medium text-sm">{label}</span>
    </label>
  );
}

export default function SurveyForm() {
  const [step, setStep] = useState<Step>("screening");
  const [data, setData] = useState<FormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const update = (field: keyof FormData, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleScreening = (answer: "yes" | "no") => {
    if (answer === "yes") {
      setStep("disqualified");
    } else {
      setStep("questions");
    }
  };

  const handleQuestionsNext = () => {
    if (!data.interestedWithBus) {
      toast.error("Please let us know if a bus service would influence your decision.");
      return;
    }
    if (!data.location) {
      toast.error("Please select your area.");
      return;
    }
    if (data.location === "Other" && !data.otherLocation.trim()) {
      toast.error("Please specify your area.");
      return;
    }
    setStep("contact");
  };

  const handleSubmit = async () => {
    if (!data.parentName.trim()) {
      toast.error("Please enter your name so we can follow up with you.");
      return;
    }
    if (!data.email) {
      toast.error("Please enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/survey", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          location: data.location === "Other" ? `Other \u2013 ${data.otherLocation}` : data.location,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Submission failed");
      }

      setStep("done");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={topRef} className="w-full max-w-2xl mx-auto">
      {step !== "disqualified" && step !== "done" && (
        <ProgressBar step={step} />
      )}

      {/* â”€â”€â”€ STEP 1: SCREENING â”€â”€â”€ */}
      {step === "screening" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-school-gold mb-2">
              Question 1 of 3
            </p>
            <CardTitle className="text-2xl font-bold text-school-navy leading-snug">
              Is your child currently enrolled at Ashton John&apos;s Private School?
            </CardTitle>
            <CardDescription className="text-base mt-2">
              This survey is intended for parents of prospective students who have not yet enrolled.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <RadioOption
              id="screening-yes"
              name="screening"
              value="yes"
              label="Yes, my child is currently enrolled"
              checked={false}
              onChange={() => handleScreening("yes")}
            />
            <RadioOption
              id="screening-no"
              name="screening"
              value="no"
              label="No, my child is not currently enrolled"
              checked={false}
              onChange={() => handleScreening("no")}
            />
          </CardContent>
        </Card>
      )}

      {/* â”€â”€â”€ DISQUALIFIED â”€â”€â”€ */}
      {step === "disqualified" && (
        <Card className="border-0 shadow-lg text-center">
          <CardContent className="py-12 flex flex-col items-center gap-4">
            <XCircle className="w-16 h-16 text-school-gold" />
            <h2 className="text-2xl font-bold text-school-navy">
              Thank you for your time!
            </h2>
            <p className="text-muted-foreground max-w-md">
              This survey is designed to gather feedback from families who are not yet part of our school community. Since your child is already enrolled, you do not need to complete this questionnaire.
            </p>
            <p className="text-muted-foreground max-w-md">
              We truly appreciate your continued support as a valued member of our school family. If you have any feedback or suggestions, please don&apos;t hesitate to{" "}
              <Link href="/contact-us" className="text-school-navy font-semibold underline underline-offset-2">
                contact us
              </Link>
              .
            </p>
          </CardContent>
        </Card>
      )}

      {/* â”€â”€â”€ STEP 2: QUESTIONS â”€â”€â”€ */}
      {step === "questions" && (
        <div className="flex flex-col gap-6">
          {/* Q2: Bus interest */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-school-gold mb-2">
                Question 2 of 3
              </p>
              <CardTitle className="text-xl font-bold text-school-navy leading-snug">
                Would you consider enrolling your child at Ashton John&apos;s Private School if we offered a school bus service?
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {[
                { value: "Yes, definitely", label: "Yes, definitely" },
                { value: "Likely yes", label: "Likely yes" },
                { value: "Maybe", label: "Maybe" },
                { value: "No", label: "No" },
              ].map((opt) => (
                <RadioOption
                  key={opt.value}
                  id={`bus-${opt.value}`}
                  name="interestedWithBus"
                  value={opt.value}
                  label={opt.label}
                  checked={data.interestedWithBus === opt.value}
                  onChange={(v) => update("interestedWithBus", v)}
                />
              ))}
            </CardContent>
          </Card>

          {/* Q3: Location */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-school-gold mb-2">
                Question 3 of 3
              </p>
              <CardTitle className="text-xl font-bold text-school-navy leading-snug">
                Which area do you live in?
              </CardTitle>
              <CardDescription>
                This helps us plan which routes to prioritise.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {LOCATIONS.map((loc) => (
                <RadioOption
                  key={loc}
                  id={`loc-${loc}`}
                  name="location"
                  value={loc}
                  label={loc}
                  checked={data.location === loc}
                  onChange={(v) => update("location", v)}
                />
              ))}
              {data.location === "Other" && (
                <div className="pl-1 pt-1">
                  <Label htmlFor="otherLocation" className="text-sm font-semibold mb-1.5 block">
                    Please specify your area
                  </Label>
                  <Input
                    id="otherLocation"
                    placeholder="Town Name"
                    value={data.otherLocation}
                    onChange={(e) => update("otherLocation", e.target.value)}
                    className="h-11"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={handleQuestionsNext}
            className="bg-school-navy hover:bg-school-navy/90 text-white h-12 text-base font-semibold"
          >
            Continue <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      )}

      {/* â”€â”€â”€ STEP 3: CONTACT â”€â”€â”€ */}
      {step === "contact" && (
        <div className="flex flex-col gap-6">
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-school-gold mb-2">
                Almost done!
              </p>
              <CardTitle className="text-2xl font-bold text-school-navy leading-snug">
                Your Contact Details
              </CardTitle>
              <CardDescription className="text-base mt-1">
                Please provide your contact details so we can follow up with you regarding the school bus service and enrolment. Your information will be kept private.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <Label htmlFor="parentName" className="text-sm font-semibold mb-1.5 block">
                  Your Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="parentName"
                  placeholder="Your full name"
                  value={data.parentName}
                  onChange={(e) => update("parentName", e.target.value)}
                  className="h-12"
                  autoComplete="name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold mb-1.5 block">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={data.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="h-12"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold mb-1.5 block">
                  Phone Number <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="e.g. 082 123 4567"
                  value={data.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="h-12"
                  autoComplete="tel"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setStep("questions")}
              className="flex-1 h-12 text-base"
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-2 bg-school-navy hover:bg-school-navy/90 text-white h-12 text-base font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" /> Submittingâ€¦
                </>
              ) : (
                "Submit Survey"
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Your information is kept private and will only be used to follow up on your school bus and enrolment enquiry.
          </p>
        </div>
      )}

      {/* ─── DONE ─── */}
      {step === "done" && (
        <Card className="border-0 shadow-lg text-center">
          <CardContent className="py-12 flex flex-col items-center gap-4">
            <CheckCircle2 className="w-16 h-16 text-school-navy" />
            <h2 className="text-2xl font-bold text-school-navy">
              Thank you for your feedback!
            </h2>
            <p className="text-muted-foreground max-w-md">
              Your response has been recorded and will help us decide whether to introduce a school bus service for our community.
            </p>
            {data.email || data.phone ? (
              <p className="text-muted-foreground max-w-md">
                We&apos;ll be in touch with updates. In the meantime, feel free to{" "}
                <Link href="/contact-us" className="text-school-navy font-semibold underline underline-offset-2">
                  contact us
                </Link>{" "}
                with any questions.
              </p>
            ) : (
              <p className="text-muted-foreground max-w-md">
                If you&apos;d like to learn more about enrolment, please{" "}
                <Link href="/contact-us" className="text-school-navy font-semibold underline underline-offset-2">
                  get in touch
                </Link>
                .
              </p>
            )}
            <Button
              asChild
              className="mt-2 bg-school-navy hover:bg-school-navy/90 text-white h-11 px-8 font-semibold"
            >
              <Link href="/">Return Home</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
